import { createAsync, query, useNavigate } from "@solidjs/router";
import {
  Accessor,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  Suspense,
} from "solid-js";
import {
  getHomeWebstoreTelescopeRouteData,
  HomeWebstoreTelescopeRouteData,
} from "./data/home_webstore_telescope_route_data";
import { HomeWebstoreTelescopeProvider } from "./context/home_webstore_telescope_context";
import { useHomeWebstoreTelescope } from "./hooks/useHomeWebstoreTelescope";
import { HeroSection } from "./fragments/hero_section";
import { PopularBrandsHeader } from "./fragments/popular_brands_header";
import { StoreBanners } from "./fragments/store_banners";
import { SectionHeader } from "./fragments/section_header";
import { BrandGridSectionWebstoreTelescope } from "~/components/webstore_telescope/brand_grid_section_webstore_telescope";
import { FooterSection } from "~/components/webstore_telescope/footer_section";
import { CategoryStripV2 } from "~/components/webstore_telescope/category_strip_v2";
import { CategoryCardV2 } from "~/components/webstore_telescope/category_card_v2";
import { CategoriesModal } from "~/components/webstore_telescope/categories_modal";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import { openModalV2 } from "~/shared_states/modal";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { useWebStoreContext } from "~/context/webstore_context";
import { RNREvent } from "~/data/events";
import { zIndexes } from "~/z_indexes";
import { TrustMakerStripTelescope } from "~/components/webstore_telescope/trust_marker_strip";

const getHomeWebstoreTelescopeRouteData$C = query(
  getHomeWebstoreTelescopeRouteData,
  "home_webstore_telescope"
);

// Client-only for optimal performance
// export default clientOnly(async () => ({ default: HomeWebstoreTelescope }), {
//   lazy: true,
// });

export default function HomeWebstoreTelescope() {
  const routeData: Accessor<HomeWebstoreTelescopeRouteData | undefined> =
    createAsync(() => getHomeWebstoreTelescopeRouteData$C(), {
      deferStream: true,
    });

  return (
    <Suspense fallback={<FullScreenLoaderTelescope />}>
      <Show when={routeData()}>
        <HomeWebstoreTelescopeContent
          routeData={routeData as Accessor<HomeWebstoreTelescopeRouteData>}
        />
      </Show>
    </Suspense>
  );
}

function HomeWebstoreTelescopeContent(props: {
  routeData: Accessor<HomeWebstoreTelescopeRouteData>;
}) {
  const navigate = useNavigate();
  const webStoreContext = useWebStoreContext();
  const homeHook = useHomeWebstoreTelescope(props.routeData);

  // Track if category strip is in sticky state (for mobile card/strip switching)
  const [isSticky, setIsSticky] = createSignal(false);
  let sentinelRef: HTMLDivElement | undefined;
  let observer: IntersectionObserver | undefined;

  onMount(() => {
    // Set up IntersectionObserver to detect when card bottom scrolls past sticky threshold
    // Sentinel is placed after the card - when it scrolls past 112px from top, switch to strip
    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Only show sticky strip when sentinel has scrolled OUT from the TOP of the viewport
        // (i.e., user scrolled down past the category card)
        // Don't show strip if sentinel is below viewport (content is long or page just loaded)
        const isAboveViewport = entry.boundingClientRect.top < 112; // 112px is the sticky offset
        setIsSticky(!entry.isIntersecting && isAboveViewport);
      },
      {
        threshold: 0,
        rootMargin: "-112px 0px 0px 0px", // Mobile sticky top offset
      }
    );

    if (sentinelRef) {
      observer.observe(sentinelRef);
    }
  });

  onCleanup(() => {
    if (observer && sentinelRef) {
      observer.unobserve(sentinelRef);
    }
  });

  const onBrandClick = (brandKey: string) => {
    navigate(`/buy-gift-card/${brandKey}`);
  };

  const openBrowseCategoriesModal = () => {
    openModalV2(
      () => <CategoriesModal categories={props.routeData()!.categories.data} />,
      {
        expandToFullHeight: true,
      }
    );
  };

  const contextValue = {
    routeData: props.routeData,
    onBrandClick,
    ...homeHook,
  };

  // Track section color index
  let sectionColorIndex = 0;

  return (
    <HomeWebstoreTelescopeProvider value={contextValue}>
      <div class="relative flex w-full flex-col items-center bg-background-normal-primary">
        {/* Hero Section */}
        <div class="mt-8 sm:mt-16"></div>
        <HeroSection />
        <div class="mt-10"></div>
        <div class="w-full px-4 sm:px-0 lg:max-w-[1024px]">
          <TrustMakerStripTelescope />
        </div>
        <div class="mt-3"></div>

        <div id="brands-section" class="w-full"></div>

        {/* Category Section */}
        <Show when={props.routeData()?.categories?.data?.length > 0}>
          {/* Mobile: Card (always visible, scrolls with page) */}
          <div class="flex w-full flex-col items-center justify-center px-3 pb-3 pt-3 sm:hidden">
            <div class="w-full max-w-[1024px]">
              <CategoryCardV2
                categories={props.routeData()!.categories.data}
                onBrowseClick={openBrowseCategoriesModal}
              />
            </div>
          </div>

          {/* Sentinel element for sticky detection - placed after card to detect when card bottom scrolls out */}
          <div
            ref={sentinelRef}
            class="h-0 w-full sm:hidden"
            aria-hidden="true"
          />

          {/* Mobile: Sticky Strip (appears when card scrolls out) */}
          {/* <Show when={isSticky()}> */}
          <div
            class="fixed top-[112px] w-full  flex-col items-center justify-center bg-background-normal-primary  px-3 pb-3 pt-3 "
            classList={{
              "flex sm:hidden": isSticky(),
              hidden: !isSticky(),
            }}
            style={{
              "z-index": zIndexes.categoryStrip,
            }}
          >
            <div class="w-full max-w-[1024px]">
              <CategoryStripV2
                categories={props.routeData()!.categories.data}
                showBrowseFirst={true}
                onBrowseClick={openBrowseCategoriesModal}
              />
            </div>
          </div>
          {/* </Show> */}

          {/* Desktop: Always show sticky Strip */}
          <div
            class="sticky top-14 hidden w-full flex-row items-end justify-center  bg-background-normal-primary px-0 pb-3 pt-3 sm:flex"
            style={{
              "z-index": zIndexes.categoryStrip,
            }}
          >
            <div class="w-full max-w-[1024px]">
              <CategoryStripV2
                categories={props.routeData()!.categories.data}
                showBrowseFirst={true}
                onBrowseClick={openBrowseCategoriesModal}
              />
            </div>
          </div>
        </Show>

        {/* Brands Section with ID for scroll */}
        {/* Popular Brands Header */}
        <PopularBrandsHeader />

        {/* Interleaved Content: Brand Grids and Sections */}
        <For each={homeHook.interleavedContent()}>
          {(chunk) => {
            if (chunk.type === "section") {
              const currentColorIndex = sectionColorIndex++;

              return (
                <Show when={chunk.section.brandList.length > 0}>
                  <div class="w-full">
                    {/* Section Header */}
                    <SectionHeader
                      section={chunk.section}
                      colorIndex={currentColorIndex}
                    >
                      <BrandGridSectionWebstoreTelescope
                        brands={chunk.section.brandList}
                        onBrandClick={onBrandClick}
                        forceDarkTheme={true}
                        categoryNameToTitleMap={homeHook.categoryNameToTitleMap()}
                      />
                    </SectionHeader>
                  </div>
                </Show>
              );
            } else {
              // Brand chunk
              return (
                <Show when={chunk.brands.length > 0}>
                  <div class="w-full py-5">
                    <BrandGridSectionWebstoreTelescope
                      brands={chunk.brands}
                      onBrandClick={onBrandClick}
                      categoryNameToTitleMap={homeHook.categoryNameToTitleMap()}
                    />
                  </div>
                  {/* Store banners sit right below the popular brands grid */}
                  <Show when={chunk.isFirst}>
                    <StoreBanners />
                  </Show>
                </Show>
              );
            }
          }}
        </For>
        <div class="max-w-[1024px] px-4 sm:px-0">
          <FooterSection
            brandDiscounts={webStoreContext.storeState.brands.map((b) => ({
              brandKey: b.brandKey,
              discount: b.voucherProduct.discountPercentage,
            }))}
          />
        </div>
      </div>

      {/* Footer */}
    </HomeWebstoreTelescopeProvider>
  );
}
